import axios from 'axios'

const API_BASE = '/api/admin'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access - redirect immediately
      localStorage.removeItem('token')
      localStorage.removeItem('admin')
      // Use replace to prevent back button returning to expired page
      window.location.replace('/login')
      // Return a never-resolving promise to prevent further execution
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/login', { email, password }),
  logout: () => api.post('/logout'),
  verifyToken: () => api.post('/verify-token'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  getCurrentAdmin: () => api.get('/me'),
  listAdmins: () => api.get('/admins/'),
  createAdmin: (data) => api.post('/create-admin', data),
  updateAdmin: (adminId, data) => api.put(`/admins/${adminId}/`, data),
  toggleAdminStatus: (adminId) => api.put(`/admins/${adminId}/toggle-active/`),
}

// Firms API
export const firmsAPI = {
  list: (params) => api.get('/firms/', { params }),
  get: (firmId) => api.get(`/firms/${firmId}/`),
  create: (data) => api.post('/firms/', data),
  update: (firmId, data) => api.put(`/firms/${firmId}/`, data),
  getUsage: (firmId) => api.get(`/firms/${firmId}/usage/`),
  suspend: (firmId) => api.post(`/firms/${firmId}/suspend/`),
  reactivate: (firmId) => api.post(`/firms/${firmId}/reactivate/`),
  getStats: () => api.get('/firms/stats/'),
}

// Metrics API
export const metricsAPI = {
  getDashboard: () => api.get('/dashboard/metrics'),
  getDashboardMetrics: () => api.get('/dashboard/metrics'),
  getActivity: (days = 7) => api.get('/metrics/activity', { params: { days } }),
  getSystem: () => api.get('/metrics/system'),
}

// ML Models API
export const mlModelsAPI = {
  list: (params) => api.get('/ml/models', { params }),
  get: (deploymentId) => api.get(`/ml/models/${deploymentId}`),
  train: (data) => api.post('/ml/train', data),
  deploy: (deploymentId) => api.post(`/ml/models/${deploymentId}/deploy`),
  updateStatus: (deploymentId, status, notes) => api.put(`/ml/models/${deploymentId}/status`, { deployment_status: status, notes }),
  rename: (deploymentId, newName) => api.put(`/ml/models/${deploymentId}/rename`, { new_name: newName }),
  delete: (deploymentId) => api.delete(`/ml/models/${deploymentId}`),
  getPerformance: () => api.get('/ml/performance'),
}

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getBillingHistory: () => api.get('/subscriptions/billing-history'),
}

// System API
export const systemAPI = {
  getConfig: () => api.get('/system/config'),
  getConfigValue: (key) => api.get(`/system/config/${key}`),
  updateConfig: (key, value) => api.put(`/system/config/${key}`, { config_value: value }),
  getAuditLog: (params) => api.get('/system/audit-log', { params }),
  getHealth: () => api.get('/system/health'),
  toggleMaintenanceMode: (enable) => api.post('/system/maintenance-mode', null, { params: { enable } }),
}

// Brokerages API
export const brokeragesAPI = {
  list: () => api.get('/brokerages/'),
  get: (connectionId) => api.get(`/brokerages/${connectionId}`),
  getStats: () => api.get('/brokerages/stats'),
  create: (data) => api.post('/brokerages/', data),
  update: (connectionId, data) => api.put(`/brokerages/${connectionId}`, data),
  refresh: (connectionId) => api.post(`/brokerages/${connectionId}/refresh`),
  delete: (connectionId) => api.delete(`/brokerages/${connectionId}`),
}

// Schwab OAuth API
export const schwabAPI = {
  initiateConnect: (firmId) => api.post('/schwab/connect', null, { params: { firm_id: firmId } }),
  getConnectionStatus: (firmId) => api.get(`/schwab/status/${firmId}`),
  refreshToken: (connectionId) => api.post(`/schwab/refresh/${connectionId}`),
}

// Portfolios API
export const portfoliosAPI = {
  list: (params) => api.get('/admin/portfolios/list', { params }),
  getStats: () => api.get('/admin/portfolios/stats'),
  getDetails: (portfolioId) => api.get(`/admin/portfolios/${portfolioId}`),
  delete: (portfolioId) => api.delete(`/admin/portfolios/${portfolioId}`),
}

// AI-Generated Portfolios API
export const aiPortfoliosAPI = {
  getHistory: (params) => api.get('/ai-portfolios/history', { params }), // Get historical portfolios (fast, no generation)
  generate: (params) => api.post('/ai-portfolios/generate', null, { params }), // Generate new portfolios (expensive!)
  getStats: () => api.get('/ai-portfolios/stats'),
  getDetails: (strategy) => api.get(`/ai-portfolios/${strategy}`),
  getPerformanceSummary: () => api.get('/ai-portfolios/performance/summary'), // Get performance metrics
  getPerformanceTimeline: (params) => api.get('/ai-portfolios/performance/timeline', { params }), // Get historical data for charts
  // View portfolio detail with Yahoo Finance links
  view: (portfolioId) => api.get(`/ai-portfolios/view/${portfolioId}`),
  getActive: () => api.get('/ai-portfolios/active'), // Get active portfolios with Yahoo links
  // Refresh prices from Polygon API (call before getActive to get live prices)
  refreshPrices: () => api.post('/ai-portfolios/refresh-prices'),
  // Delete portfolios
  delete: (portfolioId) => api.delete(`/ai-portfolios/${portfolioId}`),
  deleteAll: (strategyType) => api.delete('/ai-portfolios/', { params: strategyType ? { strategy_type: strategyType } : {} }),
  // Deprecated: list - use getHistory() instead
  list: (params) => api.get('/ai-portfolios/history', { params }),

  // NEW: Enhanced Performance Tracking API
  getPerformanceMetrics: (portfolioId, days = 30) =>
    api.get(`/ai-portfolios/performance/metrics/${portfolioId}`, { params: { days } }), // Detailed metrics
  getChartData: (portfolioId, days = 90) =>
    api.get(`/ai-portfolios/performance/chart-data/${portfolioId}`, { params: { days } }), // Chart-ready cumulative returns
  getStrategyComparison: () =>
    api.get('/ai-portfolios/performance/strategy-comparison'), // Compare all strategies
  getRebalanceCandidates: (driftThreshold = 0.05) =>
    api.get('/ai-portfolios/performance/rebalance-candidates', { params: { drift_threshold: driftThreshold } }), // Portfolios needing rebalance
  getDashboardSummary: () =>
    api.get('/ai-portfolios/performance/dashboard-summary'), // High-level dashboard stats

  // Position History API (tracks superseded positions from weekly rebalancing)
  getPositionHistory: (params) =>
    api.get('/ai-portfolios/position-history', { params }), // List all superseded positions with filters
  getPositionHistoryByTicker: (ticker) =>
    api.get(`/ai-portfolios/position-history/by-ticker/${ticker}`), // Position history for specific ticker
  getPositionHistorySummary: () =>
    api.get('/ai-portfolios/position-history/summary'), // Aggregated summary from DB views

  // Rebalance Events API (weekly portfolio regeneration tracking)
  getRebalanceEvents: (params) =>
    api.get('/ai-portfolios/rebalance-events', { params }), // List portfolio generations/rebalance events
  getRebalanceChanges: (portfolioId) =>
    api.get(`/ai-portfolios/rebalance-events/${portfolioId}/changes`), // Detailed changes for specific rebalance
}

// Master Portfolios API (continuous portfolio management)
export const masterPortfoliosAPI = {
  // List all master portfolios
  list: () => api.get('/master-portfolios/'),

  // Get specific master portfolio with positions
  get: (strategyType) => api.get(`/master-portfolios/${strategyType}`),

  // Get position change history for a specific strategy
  getPositionChanges: (strategyType, limitOrParams = 50) => {
    const params = typeof limitOrParams === 'number' ? { limit: limitOrParams } : limitOrParams
    return api.get(`/master-portfolios/${strategyType}/position-changes`, { params })
  },

  // Get all position changes across all strategies (or filtered)
  getAllPositionChanges: (params = {}) =>
    api.get('/master-portfolios/position-changes/all', { params }),

  // Get daily values for charting
  getDailyValues: (strategyType, days = 30) =>
    api.get(`/master-portfolios/${strategyType}/daily-values`, { params: { days } }),

  // Get all client subscriptions
  getSubscriptions: (params) =>
    api.get('/master-portfolios/subscriptions/all', { params }),

  // Get overall stats
  getStats: () => api.get('/master-portfolios/stats/summary'),

  // Get recent evaluations
  getRecentEvaluations: (limit = 20) =>
    api.get('/master-portfolios/evaluations/recent', { params: { limit } }),
}

// Auto-Trading API
export const autoTradingAPI = {
  // Client approval
  approveClient: (clientId, settings) =>
    api.post(`/auto-trading/clients/${clientId}/approve`, settings),
  getClientSettings: (clientId) => api.get(`/auto-trading/clients/${clientId}/settings`),

  // Portfolio AI configuration
  configurePortfolioAI: (portfolioId, settings) =>
    api.post(`/auto-trading/portfolios/${portfolioId}/ai-settings`, settings),

  // Trade recommendations
  generateRecommendations: (portfolioId) =>
    api.post(`/auto-trading/portfolios/${portfolioId}/generate-recommendations`),
  listRecommendations: (params) => api.get('/auto-trading/recommendations', { params }),
  approveRecommendation: (recommendationId) =>
    api.post(`/auto-trading/recommendations/${recommendationId}/approve`),
  rejectRecommendation: (recommendationId, reason) =>
    api.post(`/auto-trading/recommendations/${recommendationId}/reject`, { reason }),

  // Statistics
  getStats: () => api.get('/auto-trading/stats'),
}

// Rebalancing API
export const rebalancingAPI = {
  // Recommendations
  getRecommendations: (params) => api.get('/rebalancing/recommendations', { params }),
  getRecommendation: (rebalanceId) => api.get(`/rebalancing/recommendations/${rebalanceId}`),
  acceptRecommendation: (rebalanceId, executionMethod = 'manual') =>
    api.post(`/rebalancing/recommendations/${rebalanceId}/accept`, { execution_method: executionMethod }),
  rejectRecommendation: (rebalanceId, reason = '') =>
    api.post(`/rebalancing/recommendations/${rebalanceId}/reject`, { reason }),
  scheduleRecommendation: (rebalanceId, scheduledFor) =>
    api.post(`/rebalancing/recommendations/${rebalanceId}/schedule`, { scheduled_for: scheduledFor }),

  // History
  getHistory: (params) => api.get('/rebalancing/history', { params }),

  // Activity Feed
  getActivity: (params) => api.get('/rebalancing/activity', { params }),
  getUnreadCount: () => api.get('/rebalancing/activity/unread-count'),
  markActivityRead: (activityId) => api.post(`/rebalancing/activity/${activityId}/mark-read`),

  // Notification Preferences
  getNotificationPreferences: () => api.get('/rebalancing/notifications/preferences'),
  updateNotificationPreferences: (preferences) =>
    api.put('/rebalancing/notifications/preferences', preferences),
}

// Pipelines API
export const pipelinesAPI = {
  list: (params) => api.get('/pipelines/', { params }),
  get: (pipelineId) => api.get(`/pipelines/${pipelineId}`),
  getStats: () => api.get('/pipelines/stats'),
  run: (pipelineId) => api.post(`/pipelines/${pipelineId}/run`),
  getExecutions: (pipelineId, params) => api.get(`/pipelines/${pipelineId}/executions`, { params }),
}

// Cron Jobs API
export const cronJobsAPI = {
  list: (params) => api.get('/cron/jobs', { params }),
  get: (jobId) => api.get(`/cron/jobs/${jobId}`),
  create: (data) => api.post('/cron/jobs', data),
  update: (jobId, data) => api.put(`/cron/jobs/${jobId}`, data),
  delete: (jobId) => api.delete(`/cron/jobs/${jobId}`),
  toggle: (jobId) => api.post(`/cron/jobs/${jobId}/toggle`),
  run: (jobId) => api.post(`/cron/jobs/${jobId}/run`),
  getExecutions: (jobId, params) => api.get(`/cron/jobs/${jobId}/executions`, { params }),
  getCategories: () => api.get('/cron/categories'),
}

// DuckDB Feature Store API
export const duckdbAPI = {
  // Statistics and metadata
  getStats: () => api.get('/duckdb/stats'),
  getSchema: () => api.get('/duckdb/schema'),
  getSampleQueries: () => api.get('/duckdb/sample-queries'),

  // Query execution
  executeQuery: (query, limit = 1000) =>
    api.post('/duckdb/query', { query, limit }),

  // Sync management
  triggerSync: (syncType, days = 7) =>
    api.post('/duckdb/sync', { sync_type: syncType, days }),
  getSyncStatus: () => api.get('/duckdb/sync/status'),
  cancelSync: () => api.post('/duckdb/sync/cancel'),
}

// PostgreSQL Database API
export const postgresAPI = {
  // Statistics and metadata
  getStats: () => api.get('/postgres/stats'),
  getTables: () => api.get('/postgres/tables'),
  getTableSchema: (schema, tableName) => api.get(`/postgres/tables/${schema}/${tableName}/schema`),
  getSampleQueries: () => api.get('/postgres/sample-queries'),

  // Query execution
  executeQuery: (query, limit = 1000) =>
    api.post('/postgres/query', { query, limit }),

  // Query analysis
  explainQuery: (query) => api.get('/postgres/explain', { params: { query } }),
}

// SEO Analytics API (Google/Bing integrations)
export const seoAPI = {
  // Status and configuration
  getStatus: () => api.get('/seo/status'),
  getDashboard: () => api.get('/seo/dashboard'),

  // Bing Webmaster Tools
  getBingTraffic: () => api.get('/seo/bing/traffic'),
  getBingCrawl: () => api.get('/seo/bing/crawl'),
  getBingKeywords: () => api.get('/seo/bing/keywords'),
  getBingPages: () => api.get('/seo/bing/pages'),

  // Google Search Console
  getGooglePerformance: (days = 28) => api.get('/seo/google/performance', { params: { days } }),
  getGoogleQueries: (days = 28, limit = 20) => api.get('/seo/google/queries', { params: { days, limit } }),
  getGooglePages: (days = 28, limit = 20) => api.get('/seo/google/pages', { params: { days, limit } }),
}

// SEO Rankings API (keyword rank tracking)
export const seoRankingsAPI = {
  // Summary and overview
  getSummary: () => api.get('/seo/rankings/summary'),
  getCurrentRankings: () => api.get('/seo/rankings/current'),
  getHistory: (keyword, days = 30) => api.get('/seo/rankings/history', { params: { keyword, days } }),
  getTrends: () => api.get('/seo/rankings/trends'),

  // Keyword management
  getKeywords: (activeOnly = true) => api.get('/seo/keywords', { params: { active_only: activeOnly } }),
  addKeyword: (data) => api.post('/seo/keywords', data),
  deleteKeyword: (id) => api.delete(`/seo/keywords/${id}`),

  // Manual check trigger
  triggerCheck: () => api.post('/seo/check'),
}

// AI Visibility API (AI search mentions and crawler tracking)
export const aiVisibilityAPI = {
  // Summary
  getSummary: (days = 30) => api.get('/ai-visibility/summary', { params: { days } }),

  // Search results
  getSearchResults: (days = 30, engineName = null, mentionedOnly = false) =>
    api.get('/ai-visibility/search-results', { params: { days, engine_name: engineName, mentioned_only: mentionedOnly } }),
  getSearchTrends: (days = 30) => api.get('/ai-visibility/search-trends', { params: { days } }),

  // Crawler activity
  getCrawlerActivity: (days = 30, botName = null) =>
    api.get('/ai-visibility/crawler-activity', { params: { days, bot_name: botName } }),
  getCrawlerSummary: (days = 30) => api.get('/ai-visibility/crawler-summary', { params: { days } }),

  // Test queries
  getQueries: (activeOnly = true) => api.get('/ai-visibility/queries', { params: { active_only: activeOnly } }),
  addQuery: (data) => api.post('/ai-visibility/queries', data),
  deleteQuery: (id) => api.delete(`/ai-visibility/queries/${id}`),

  // Check triggers
  triggerSearchCheck: (allQueries = false) => api.post('/ai-visibility/check-search', null, { params: { all_queries: allQueries } }),
  triggerCrawlerCheck: () => api.post('/ai-visibility/check-crawlers'),

  // Config status
  getConfigStatus: () => api.get('/ai-visibility/config-status'),
}

// Model Validation API
export const validationAPI = {
  // Summary and overview
  getSummary: () => api.get('/validation/summary'),

  // Walk-forward validation results
  getWalkForwardResults: (params) => api.get('/validation/walk-forward', { params }),

  // Prediction tracking stats
  getPredictionStats: () => api.get('/validation/predictions/stats'),

  // Degradation alerts
  getAlerts: () => api.get('/validation/alerts'),

  // Thresholds configuration
  getThresholds: () => api.get('/validation/thresholds'),

  // Trigger validation run
  runValidation: (modelName, months = 3) =>
    api.post('/validation/run', { model_name: modelName, months }),

  // Trigger backfill of actual returns
  triggerBackfill: (daysBack = 30) =>
    api.post('/validation/backfill', null, { params: { days_back: daysBack } }),
}

// Blog Management API
export const blogAPI = {
  // Posts CRUD
  listPosts: (params) => api.get('/blog/posts', { params }),
  getPost: (id) => api.get(`/blog/posts/${id}`),
  createPost: (data) => api.post('/blog/posts', data),
  updatePost: (id, data) => api.put(`/blog/posts/${id}`, data),
  deletePost: (id, hardDelete = false) => api.delete(`/blog/posts/${id}`, { params: { hard_delete: hardDelete } }),
  publishPost: (id) => api.post(`/blog/posts/${id}/publish`),
  unpublishPost: (id) => api.post(`/blog/posts/${id}/unpublish`),

  // Categories CRUD
  listCategories: () => api.get('/blog/categories'),
  createCategory: (data) => api.post('/blog/categories', data),
  updateCategory: (id, data) => api.put(`/blog/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/blog/categories/${id}`),

  // Image management
  uploadImage: (formData) => api.post('/blog/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listImages: () => api.get('/blog/images'),
  deleteImage: (filename) => api.delete(`/blog/images/${filename}`),
}

export default api
